<template>
    <section class="relative">
        <AtomTitle class="text-center"> Остались вопросы? </AtomTitle>
        <div class="mt-10 flex w-full justify-center">
            <div class="container relative">
                <div
                    class="relative z-20 mx-auto w-full max-w-[40.75rem] rounded-12 bg-accent-100/10 p-3 md:px-30 md:py-20 lg:backdrop-blur-sm">
                    <p class="text-16/148 font-medium text-ash-200">
                        Оставьте почту или номер телефона. Наш менеджер свяжется
                        с&nbsp;Вами для подробной консультации.
                    </p>
                    <form class="mt-10">
                        <div class="space-y-5">
                            <AtomField
                                id="name"
                                v-model="formData.name"
                                placeholder="Ваше имя"
                                :error="errors.name"
                                @input="errors.name = ''" />
                            <AtomField
                                id="phone"
                                v-model="formData.phone"
                                placeholder="Телефон"
                                type="tel"
                                pattern="tel"
                                :error="errors.phone"
                                @input="
                                    validatePhone(formData, errors, $event)
                                " />
                            <AtomField
                                id="email"
                                v-model="formData.email"
                                placeholder="Электронная почта"
                                type="email"
                                :error="errors.email"
                                @input="validateEmail(formData, errors)" />
                            <AtomTextarea
                                id="message"
                                v-model="formData.message"
                                placeholder="Комментарий"
                                :error="errors.message" />
                        </div>
                        <div
                            class="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row">
                            <AtomButton
                                class="text-nowrap max-sm:w-full"
                                @click="handleForm">
                                Запросить связь
                            </AtomButton>
                            <BaseCheckbox
                                required
                                :error="errors.termsAccepted"
                                v-model="formData.termsAccepted"
                                class="!w-full md:max-w-[220px] md:shrink-0"
                                @click="errors.termsAccepted = false">
                                <span
                                    class="inline-block text-12/140 text-ash-200 max-md:text-left">
                                    Я даю&nbsp;согласие на
                                    <NuxtLink
                                        target="_blank"
                                        href="/pdf/soglasie.pdf"
                                        class="underline underline-offset-4">
                                        обработку персональных данных
                                    </NuxtLink>
                                </span>
                            </BaseCheckbox>
                        </div>
                    </form>
                </div>
                <QuestionIcon
                    class="absolute left-0 top-[8%] z-30 hidden size-[13.5rem] rotate-[-23deg] lg:left-[-5%] lg:block lg:size-[20rem] xl:left-[9%]" />
            </div>

            <div class="container absolute inset-0 size-full overflow-hidden">
                <QuestionIcon
                    class="absolute right-0 top-2/3 hidden size-[9rem] rotate-[28deg] lg:right-[2%] lg:top-[55%] lg:block lg:size-[13.75rem] xl:right-[15%] xl:top-1/2" />
            </div>
        </div>

        <picture>
            <source
                srcset="
                    /assets/images/ui/blurs/remaining-questions-sections/webp/questionnaire-form.webp
                "
                type="image/webp" />

            <img
                width="2071"
                height="1293"
                src="/assets/images/ui/blurs/remaining-questions-sections/png/questionnaire-form.png"
                alt=""
                class="absolute left-1/2 top-0 hidden h-auto w-full max-w-screen-xl -translate-x-1/2 lg:block" />
        </picture>
    </section>
</template>

<script setup lang="ts">
import AtomButton from '~/components/atom/atom-button/AtomButton.vue'
import AtomField from '~/components/atom/atom-field/AtomField.vue'
import AtomTextarea from '~/components/atom/atom-textarea/AtomTextarea.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseCheckbox from '~/components/base/base-checkbox/BaseCheckbox.vue'
import QuestionIcon from '~/components/particles/remaining-questions-section/svg/QuestionIcon.vue'

import { validateEmail, validatePhone } from '~/utils/validations'

const formData = reactive({
    name: '',
    phone: '',
    email: '',
    message: '',
    termsAccepted: false,
})

const errors = reactive({
    name: '',
    phone: '',
    email: '',
    message: '',
    termsAccepted: false,
})

async function handleForm(e: FormDataEvent) {
    e.preventDefault()

    validateData()

    if (
        !errors.name.length &&
        !errors.phone.length &&
        !errors.email.length &&
        !errors.termsAccepted &&
        !errors.message.length
    ) {
        const utmParams = useCookie<Record<string, any> | null>('utm')

        const { error } = await useApiFetch('/v1/callbacks', {
            method: 'POST',
            key: `callback-${Date.now()}`,
            body: JSON.stringify({
                name: formData.name,
                phone: formData.phone,
                form: 'Общая форма "Остались вопросы?"',
                href: location.href,
                email: formData.email,
                message: formData.message,
            }),
            query: utmParams.value || undefined,
        })

        if (error.value && error.value?.data) {
            errorHandling(error.value, errors)
            return
        }

        formData.name = ''
        formData.phone = ''
        formData.email = ''
        formData.message = ''
        formData.termsAccepted = false
    }
}

function validateData() {
    errors.name = !formData.name.length ? 'Заполните поле' : ''

    if (!formData.phone.length) {
        errors.phone = 'Заполните поле'
    }

    errors.message = !formData.message.length ? 'Заполните поле' : ''

    if (!formData.termsAccepted) {
        errors.termsAccepted = true
    }

    if (!formData.email.length) {
        errors.email = 'Заполните поле'
    } else {
        const isEmail = new RegExp(/^\S+@\S+\.\S+$/, 'i')
        errors.email = isEmail.test(formData.email)
            ? ''
            : 'Неверный формат почты'
    }
}
</script>
